/*
 * WeeklyHealthBriefing — Stage 15 P2.
 *
 * Calm, premium recap of the user's last 7 days, powered by
 * `/api/home/weekly-briefing`.
 *
 * Self-hides when `active=false` (not enough activity yet — no empty-state
 * clutter on Day 1). Renders three plain-English bullets, up to two
 * questions worth asking the doctor, and one calm next-step CTA.
 */
import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from '@phosphor-icons/react';
import { useSWRCache } from '../../services/useSWRCache';

export const WeeklyHealthBriefing = () => {
  const navigate = useNavigate();
  // 5-min TTL — the underlying data only changes when the user uploads,
  // chats, or schedules. Stale-while-revalidate covers the rest.
  const { data } = useSWRCache('/home/weekly-briefing', { ttlMs: 5 * 60_000 });

  if (!data || !data.active) return null;

  return (
    <motion.section
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.32 }}
      className="rounded-2xl border border-[#ECE8E1] bg-white p-5 lg:p-6"
      data-testid="weekly-health-briefing"
    >
      <div className="flex items-baseline justify-between gap-3 mb-3">
        <p className="text-[10px] tracking-[0.18em] uppercase text-slate-400 font-medium">
          Your week in health
        </p>
        <span className="text-[10px] text-slate-400">{data.window}</span>
      </div>

      {data.what_changed?.length > 0 && (
        <ul className="space-y-2 mb-4" data-testid="weekly-briefing-changes">
          {data.what_changed.map((line, i) => (
            <li key={i} className="flex gap-2.5 text-[13.5px] text-slate-700 leading-relaxed">
              <span className="shrink-0 w-1.5 h-1.5 rounded-full bg-[#235347] mt-[7px]" />
              <span>{line}</span>
            </li>
          ))}
        </ul>
      )}

      {data.questions?.length > 0 && (
        <div className="border-t border-[#ECE8E1] pt-4 mb-4">
          <p className="text-[10px] tracking-[0.18em] uppercase text-slate-400 font-medium mb-2">
            Worth asking
          </p>
          <ul className="space-y-1.5" data-testid="weekly-briefing-questions">
            {data.questions.map((q, i) => (
              <li
                key={i}
                className="text-[13.5px] text-slate-600 italic leading-relaxed"
                style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}
              >
                "{q}"
              </li>
            ))}
          </ul>
        </div>
      )}

      {data.next_step && (
        <button
          onClick={() => data.next_step_href && navigate(data.next_step_href)}
          className="inline-flex items-center gap-1.5 rounded-full bg-[#0E1C2E] text-white text-[12.5px] font-semibold px-3.5 py-1.5 hover:bg-[#1A2A3F] transition-colors"
          data-testid="weekly-briefing-next-step"
        >
          {data.next_step}
          <ArrowRight size={12} weight="bold" />
        </button>
      )}
    </motion.section>
  );
};

export default WeeklyHealthBriefing;
